import type { Dispatch, SetStateAction } from 'react';
import React, { createRef, useCallback, useEffect, useState } from 'react';
import { isSafari } from 'react-device-detect';
import type { ChatMessage } from '../../stores/chat.store';
import { Button, customDateFormatter, intl, Tool } from 'ehs-react';
import { usePortletStore } from '../../App';
import { decodeEscapedHtml } from 'virtual-visit-common';

const webColors: string[] = [
  'rgba(156, 39, 176, 0.65)', // Purple
  'rgba(33, 150, 243, 0.65)', // Blue
  'rgba(76, 175, 80, 0.65)', // Green
  'rgba(255, 235, 59, 0.65)', // Yellow
  'rgba(255, 152, 0, 0.65)', // Orange
  'rgba(121, 85, 72, 0.65)', // Brown
  'rgba(96, 125, 139, 0.65)', // Slate gray
  'rgba(255, 64, 129, 0.65)', // Pink
  'rgba(0, 150, 136, 0.65)', // Teal
  'rgba(255, 87, 34, 0.65)', // Deep orange
  'rgba(103, 58, 183, 0.65)', // Deep purple
  'rgba(0, 188, 212, 0.65)', // Cyan
  'rgba(233, 30, 99, 0.65)', // Pinkish red
  'rgba(63, 81, 181, 0.65)', // Indigo
  'rgba(205, 220, 57, 0.65)' // Lime
];
const scrollUpRef = createRef<HTMLSpanElement>();
const scrollToBottomRef = createRef<HTMLSpanElement>();
const chatInputRef = createRef<HTMLDivElement>();
const usedColors: { [name: string]: string } = {};

function getPhysicianChatMessageColor(msg: ChatMessage): string | undefined {
  if (msg.isPatient) {
    // patient -> use always the same default color
    return undefined;
  }

  const numColors = webColors.length;
  if (usedColors[msg.chatMessage.authorName]) {
    return usedColors[msg.chatMessage.authorName];
  } else {
    const color = webColors[Object.keys(usedColors).length % numColors];
    usedColors[msg.chatMessage.authorName] = color;
    return color;
  }
}
export interface ChatProps {
  chatMessages: ChatMessage[];
  sendChatMessage: (message: string) => void;
  toggleVideoChatView?: () => void;
}

export function Chat(props: ChatProps): JSX.Element {
  const [newMsg, setNewMsg] = useState('');
  const [maxChatMessageCharCount] = usePortletStore(state => [
    state.configuration.maxChatMessageCharCount
  ]);

  const sendMsg = () => {
    props.sendChatMessage(newMsg);
    setNewMsg('');
  };
  const onKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    if (evt.key === 'Enter') {
      sendMsg();
    }
  };
  const onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setNewMsg(evt.target.value);
  };
  const [chatContainerClass, setChatContainerClass] = useState(
    'vvpp-chat-container'
  );
  const onFocus = () => {
    setTimeout(() => {
      if (isSafari && props.chatMessages?.length <= 3) {
        setChatContainerClass('vvpp-chat-container vvpp-chat-container-focus');
      } else {
        setChatContainerClass('vvpp-chat-container');
      }
    }, 100);
  };
  const onBlur = () => {
    if (isSafari) {
      setChatContainerClass('vvpp-chat-container');
    }
  };
  const viewportHandler = useCallback(
    (event: any) => {
      if (props.chatMessages?.length <= 3) {
        // scroll to the top and fix the chat input at the virtual keyboard
        scrollUpRef.current?.scrollIntoView();
        const bottomOffset = Math.max(
          0,
          window.innerHeight -
            (window.visualViewport as any).height -
            (window.visualViewport as any).offsetTop
        ).toString();
        chatInputRef.current?.setAttribute(
          'style',
          'bottom : ' + bottomOffset + 'px;'
        );
        event.stopImmediatePropagation();
        event.stopPropagation();
      }
    },
    [props.chatMessages]
  );
  useEffect(() => {
    if (!isSafari) {
      (window.visualViewport as any).addEventListener(
        'resize',
        viewportHandler
      );
    }
    return () => {
      (window.visualViewport as any).removeEventListener(
        'resize',
        viewportHandler
      );
    };
  }, [viewportHandler]);

  useEffect(() => {
    if (props.chatMessages?.length > 3) {
      // scroll to the last message
      scrollToBottomRef.current?.scrollIntoView();
      chatInputRef.current?.setAttribute('style', 'bottom : ' + 0 + 'px;');
    }
  }, [props.chatMessages, props]);

  return (
    <>
      <ChatHeader />
      {props.toggleVideoChatView && (
        <BackButton
          toggleVideoChatView={props.toggleVideoChatView}
          setShatContainerClass={setChatContainerClass}
        />
      )}
      <span ref={scrollUpRef} />
      <div className={chatContainerClass}>
        {props.chatMessages?.map(msg => (
          <Message key={msg.id} msg={msg} />
        ))}
        <span ref={scrollToBottomRef} />
      </div>
      <div ref={chatInputRef} className="vvpp-chat-input-container">
        <input
          type="text"
          placeholder={intl('common.messaging.input.label')}
          value={newMsg}
          onKeyDown={onKeyDown}
          onChange={onChange}
          maxLength={maxChatMessageCharCount}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        <Tool
          icon="send_message"
          onClick={sendMsg}
          text=""
          aria-label={intl('virtualvisitpatientportlet.send.chat.message')}
        />
      </div>
    </>
  );
}

function ChatHeader(): JSX.Element {
  return (
    <h3 className="vvpp-chat-header">
      {intl('virtualvisitoverviewportlet.chat.tab.title')}
    </h3>
  );
}

function Message(props: { msg: ChatMessage }): JSX.Element {
  const senderReceiverCls = props.msg.sender
    ? 'vvpp-chat-message-bubble-sent'
    : 'vvpp-chat-message-bubble-received';
  const msgBubbleCls = `vvpp-chat-message-bubble ${senderReceiverCls}`;
  const formatDateTime = (dateTimeStr: string) => {
    const df = customDateFormatter({
      hour: '2-digit',
      minute: '2-digit'
    });
    return df.formatDate(dateTimeStr);
  };

  const col = getPhysicianChatMessageColor(props.msg);

  return (
    <div className="vvpp-chat-message-container" role="alert">
      <div
        className={msgBubbleCls}
        style={col ? { borderLeft: `0.5rem solid ${col}` } : {}}
      >
        <div className="vvpp-chat-message-header">
          <div>{decodeEscapedHtml(props.msg.chatMessage.authorName)}</div>
          <div>{formatDateTime(props.msg.chatMessage.dateTimeIsoString)}</div>
        </div>
        <div className="vvpp-chat-message-body">
          {props.msg.sender
            ? props.msg.chatMessage.chatMessageBody
            : decodeEscapedHtml(props.msg.chatMessage.chatMessageBody)}
        </div>
      </div>
    </div>
  );
}

function BackButton(props: {
  toggleVideoChatView: () => void;
  setShatContainerClass: Dispatch<SetStateAction<string>>;
}): JSX.Element {
  const backButtonClick = () => {
    props.setShatContainerClass('vvpp-chat-container');
    props.toggleVideoChatView();
  };
  return (
    <Button
      className="vvpp-chat-back-to-video"
      variant="primary"
      icon="arrow-left"
      text={intl('virtualvisitpatientportlet.open.video.panel')}
      onClick={backButtonClick}
    />
  );
}
